
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Download, FileText, BarChart3, Calendar } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";

interface ReportFilters {
  startDate: string;
  endDate: string;
  status: string;
  paymentStatus: string;
  includeItems: boolean;
  includeCustomer: boolean;
  groupBy: string;
}

interface OrderWithProfile {
  id: string;
  user_id: string;
  total_amount: number;
  status: string;
  payment_status: string;
  payment_method: string;
  created_at: string;
  updated_at: string;
  tracking_number?: string;
  order_items?: Array<{
    id: string;
    quantity: number;
    price: number;
    products: {
      name: string;
      sku: string;
    } | null;
  }>;
  profile?: {
    full_name: string;
    email: string;
  } | null;
}

const OrderReportGenerator = () => {
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    status: 'all',
    paymentStatus: 'all',
    includeItems: true,
    includeCustomer: true,
    groupBy: 'none'
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['order-report', filters],
    queryFn: async (): Promise<OrderWithProfile[]> => {
      let query = supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            quantity,
            price,
            products (
              name,
              sku
            )
          )
        `);

      // Apply date filters
      if (filters.startDate) {
        query = query.gte('created_at', startOfDay(new Date(filters.startDate)).toISOString());
      }
      if (filters.endDate) {
        query = query.lte('created_at', endOfDay(new Date(filters.endDate)).toISOString());
      }

      // Apply status filters
      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.paymentStatus !== 'all') {
        query = query.eq('payment_status', filters.paymentStatus);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      // Fetch profile information separately if needed
      if (filters.includeCustomer && data) {
        const userIds = data.map(order => order.user_id).filter(Boolean);
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .in('id', userIds);

          // Map profiles to orders
          return data.map(order => ({
            ...order,
            profile: profiles?.find(p => p.id === order.user_id) || null
          }));
        }
      }

      return data || [];
    },
    enabled: !!filters.startDate && !!filters.endDate,
  });

  const generateCSVReport = () => {
    if (!reportData) return;

    setIsGenerating(true);

    const headers = [
      'Order ID',
      'Date',
      'Status',
      'Payment Status',
      'Total Amount',
      ...(filters.includeCustomer ? ['Customer Name', 'Customer Email'] : []),
      ...(filters.includeItems ? ['Items Count', 'Item Details'] : [])
    ];

    const rows = reportData.map(order => {
      const row = [
        order.id,
        format(new Date(order.created_at), 'yyyy-MM-dd HH:mm'),
        order.status,
        order.payment_status,
        order.total_amount,
        ...(filters.includeCustomer ? [
          order.profile?.full_name || 'N/A',
          order.profile?.email || 'N/A'
        ] : []),
        ...(filters.includeItems ? [
          order.order_items?.length || 0,
          order.order_items?.map(item => 
            `${item.products?.name} (${item.quantity}x)`
          ).join('; ') || 'N/A'
        ] : [])
      ];
      return row;
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-report-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    setIsGenerating(false);
  };

  const generateJSONReport = () => {
    if (!reportData) return;

    setIsGenerating(true);

    const reportSummary = {
      filters,
      generatedAt: new Date().toISOString(),
      totalOrders: reportData.length,
      totalRevenue: reportData.reduce((sum, order) => sum + Number(order.total_amount), 0),
      statusBreakdown: reportData.reduce((acc: any, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {}),
      paymentStatusBreakdown: reportData.reduce((acc: any, order) => {
        acc[order.payment_status] = (acc[order.payment_status] || 0) + 1;
        return acc;
      }, {}),
      orders: reportData
    };

    const blob = new Blob([JSON.stringify(reportSummary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-report-${filters.startDate}-to-${filters.endDate}.json`;
    a.click();
    URL.revokeObjectURL(url);

    setIsGenerating(false);
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <CardTitle className="text-admin-foreground flex items-center space-x-2">
          <FileText className="h-5 w-5" />
          <span>Order Report Generator</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-admin-foreground">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="bg-admin-card border-admin-border text-admin-foreground"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate" className="text-admin-foreground">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="bg-admin-card border-admin-border text-admin-foreground"
            />
          </div>
        </div>

        {/* Status Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-admin-foreground">Order Status</Label>
            <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
              <SelectTrigger className="bg-admin-card border-admin-border text-admin-foreground">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-admin-foreground">Payment Status</Label>
            <Select value={filters.paymentStatus} onValueChange={(value) => setFilters(prev => ({ ...prev, paymentStatus: value }))}>
              <SelectTrigger className="bg-admin-card border-admin-border text-admin-foreground">
                <SelectValue placeholder="Select payment status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Include Options */}
        <div className="space-y-4">
          <Label className="text-admin-foreground">Include in Report</Label>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeItems"
                checked={filters.includeItems}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, includeItems: checked as boolean }))}
              />
              <Label htmlFor="includeItems" className="text-admin-foreground">Order Items</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeCustomer"
                checked={filters.includeCustomer}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, includeCustomer: checked as boolean }))}
              />
              <Label htmlFor="includeCustomer" className="text-admin-foreground">Customer Details</Label>
            </div>
          </div>
        </div>

        {/* Report Summary */}
        {reportData && (
          <div className="bg-admin-muted/20 rounded-lg p-4 space-y-2">
            <h3 className="text-admin-foreground font-medium">Report Preview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-admin-muted-foreground">Total Orders:</span>
                <span className="text-admin-foreground font-medium ml-2">{reportData.length}</span>
              </div>
              <div>
                <span className="text-admin-muted-foreground">Total Revenue:</span>
                <span className="text-admin-foreground font-medium ml-2">
                  ${reportData.reduce((sum, order) => sum + Number(order.total_amount), 0).toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-admin-muted-foreground">Date Range:</span>
                <span className="text-admin-foreground font-medium ml-2">
                  {format(new Date(filters.startDate), 'MMM dd')} - {format(new Date(filters.endDate), 'MMM dd')}
                </span>
              </div>
              <div>
                <span className="text-admin-muted-foreground">Avg Order:</span>
                <span className="text-admin-foreground font-medium ml-2">
                  ${reportData.length > 0 ? (reportData.reduce((sum, order) => sum + Number(order.total_amount), 0) / reportData.length).toFixed(2) : '0.00'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Generate Buttons */}
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={generateCSVReport}
            disabled={!reportData || isGenerating || isLoading}
            className="bg-admin-accent-green hover:bg-admin-accent-green/90 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            {isGenerating ? 'Generating...' : 'Export CSV'}
          </Button>
          <Button
            onClick={generateJSONReport}
            disabled={!reportData || isGenerating || isLoading}
            variant="outline"
            className="border-admin-border text-admin-foreground hover:bg-admin-muted/20"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {isGenerating ? 'Generating...' : 'Export JSON'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderReportGenerator;
